/**
 * RBAC Fallback Helper
 *
 * TODO: feature_flag_cleanup: hasAccountPermissions - Remove this file when FF is fully rolled out.
 * This provides backwards compatibility by falling back to role-based access control
 * when the PBAC feature flag is disabled.
 *
 * To clean up:
 * 1. Delete this file
 * 2. Remove imports and usage from usePermissionAccess.ts, WithPermissionAccess.tsx, RestrictedLink.tsx
 * 3. Delete PERMISSION_TO_ROLE_MAP from permissionTypes.ts
 */
import { PERMISSION_TO_ROLE_MAP, Permission } from '@/utils/permissionTypes';
import { UserRole, hasRequiredRole } from '@/utils/roleUtils';

/**
 * Gets the required role for a permission from PERMISSION_TO_ROLE_MAP.
 */
const getRoleForPermission = (permission: string): UserRole | undefined => {
  return PERMISSION_TO_ROLE_MAP[permission as Permission];
};

/**
 * Checks if user has the required role for a permission.
 * Used internally by usePermissionAccess hook.
 */
export const hasRoleForPermission = (
  permission: string,
  userRole: UserRole | null,
): boolean => {
  const requiredRole = getRoleForPermission(permission);
  if (!requiredRole) return true;
  return hasRequiredRole(userRole, requiredRole);
};

interface CheckAccessParams {
  permission?: string;
  role?: UserRole;
  hasAccountPermissions: boolean;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: UserRole) => boolean;
}

/**
 * Unified access check that handles both PBAC and RBAC.
 *
 * When PBAC is ON: Uses hasPermission(permission)
 * When PBAC is OFF: Uses explicit role if provided, otherwise uses hasPermission
 *   (which internally checks PERMISSION_TO_ROLE_MAP)
 */
export const checkAccess = ({
  permission,
  role,
  hasAccountPermissions,
  hasPermission,
  hasRole,
}: CheckAccessParams): boolean => {
  if (!permission) return true;

  if (hasAccountPermissions) {
    return hasPermission(permission);
  }

  return role ? hasRole(role) : hasPermission(permission);
};
